import { useParams, Navigate, Link } from "react-router-dom";
import { MessageCircle } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { waLink } from "@/lib/api";
import { Reveal } from "@/components/store/Reveal";

const POLICIES = {
  terms: {
    title: "Terms of Service",
    intro:
      "Welcome to 17/11 BeautyBarn. By placing an order on our website, you agree to the terms below.",
    sections: [
      {
        heading: "Our products",
        points: [
          "Our products are skincare formulations for external use only.",
          "Results may vary from person to person depending on your skin type — please always do a patch test before full use.",
        ],
      },
      {
        heading: "Pricing & availability",
        points: [
          "We reserve the right to update our prices and product range at any time without prior notice.",
          "All prices are listed in Nigerian Naira (₦) and payment is processed securely through Paystack.",
        ],
      },
    ],
  },
  privacy: {
    title: "Privacy Policy",
    intro:
      "We respect your privacy and are committed to protecting the personal information you share with us.",
    sections: [
      {
        heading: "Data we collect",
        points: [
          "Your name, phone number, email address and delivery address — collected only to process your order and keep you updated.",
        ],
      },
      {
        heading: "How we use it",
        points: [
          "To process orders, arrange delivery, provide customer support and send you order updates.",
          "We do NOT sell or share your data with third parties.",
        ],
      },
      {
        heading: "Your rights",
        points: [
          "You can request that we delete your information at any time by contacting us on WhatsApp.",
          "Payment details are handled securely by Paystack — we never see or store your card information.",
        ],
      },
    ],
  },
  refund: {
    title: "Refund Policy",
    intro:
      "Because skincare is a hygiene-sensitive product, please read our refund terms carefully before ordering.",
    sections: [
      {
        points: [
          "No returns on opened skincare products, for hygiene reasons.",
          "Refunds or replacements are only offered if you receive a damaged, wrong or defective item. Notify us with a photo or video within 24 hours of delivery.",
          "Approved refunds are processed to your original payment method within 3–5 working days.",
          "We do not offer refunds for change of mind.",
        ],
      },
    ],
  },
  shipping: {
    title: "Shipping & Delivery",
    intro:
      "Here's everything you need to know about how and when your order reaches you.",
    sections: [
      {
        points: [
          "Location: We deliver within Port Harcourt and nationwide across Nigeria.",
          "Processing time: Orders are processed within 1–2 business days.",
          "Delivery time: Port Harcourt 1–3 days; other states 3–7 days. Delays may occasionally occur due to the courier.",
          "Shipping fee: Calculated at checkout based on your delivery location.",
          "Risk: Once your order is handed to the courier, we are not liable for delays or loss — please provide a correct address and phone number.",
        ],
      },
    ],
  },
  cookies: {
    title: "Cookie & Consent Notice",
    intro:
      "17/11 BeautyBarn uses cookies to give you a smooth, personalised shopping experience.",
    sections: [
      {
        heading: "What we use cookies for",
        points: [
          "To make the site work — remembering your cart and keeping you logged in.",
          "To improve your experience — understanding which products you like.",
          "For customer support — processing your orders with your name, email and address.",
        ],
      },
      {
        heading: "Your consent",
        points: [
          "We do NOT sell your data.",
          "By using our website, you consent to our use of cookies.",
          "You can disable cookies in your browser settings at any time.",
        ],
      },
    ],
  },
  disclaimer: {
    title: "Skincare Disclaimer",
    intro:
      "The skincare products from 17/11 BeautyBarn are made to nourish, glow, and improve the appearance of your skin. Please read the notes below before use.",
    sections: [
      {
        heading: "Results vary",
        points: [
          "Results depend on your skin type, consistency of use, and body chemistry. What works beautifully for one person may not work the same way for another.",
        ],
      },
      {
        heading: "For external use only",
        points: [
          "Do not ingest our products.",
          "Avoid contact with the eyes.",
        ],
      },
      {
        heading: "Not medical advice",
        points: [
          "Our products are cosmetic and are not intended to diagnose, treat, cure or prevent any skin disease.",
          "For medical skin conditions, please consult a dermatologist.",
        ],
      },
      {
        heading: "If irritation occurs",
        points: [
          "Discontinue use immediately if you experience irritation, redness or discomfort, and contact us.",
          "By purchasing, you confirm that you have read and understood this disclaimer, and that 17/11 BeautyBarn is not liable for misuse or allergic reactions.",
        ],
      },
    ],
  },
};

export default function Policy() {
  const { slug } = useParams();
  const { settings } = useStore();
  const policy = POLICIES[slug];

  if (!policy) return <Navigate to="/" replace />;

  return (
    <div className="pt-28 md:pt-36 pb-24" data-testid={`policy-${slug}`}>
      <div className="max-w-3xl mx-auto px-5 md:px-8">
        <Reveal>
          <p className="overline text-terracotta mb-3">Legal</p>
          <h1 className="font-serif text-5xl md:text-6xl text-espresso tracking-tight mb-5">{policy.title}</h1>
          <p className="text-bark text-lg leading-relaxed mb-12">{policy.intro}</p>
        </Reveal>

        <div className="space-y-10">
          {policy.sections.map((s, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <section className="border-t border-sandline pt-6">
                {s.heading && <h2 className="font-serif text-2xl text-espresso mb-4">{s.heading}</h2>}
                <ul className="space-y-3">
                  {s.points.map((p, k) => (
                    <li key={k} className="flex gap-3 text-bark leading-relaxed">
                      <span className="text-terracotta mt-2 w-1.5 h-1.5 rounded-full bg-terracotta shrink-0" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </section>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <div className="mt-14 bg-sand rounded-2xl p-7 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <p className="font-serif text-xl text-espresso mb-1">Have a question?</p>
              <p className="text-bark text-sm">We&apos;re always happy to help on WhatsApp.</p>
            </div>
            <a
              href={waLink(settings.whatsapp, `Hi 17/11 BeautyBarn, I have a question about your ${policy.title}.`)}
              target="_blank"
              rel="noreferrer"
              data-testid="policy-whatsapp"
              className="inline-flex items-center gap-2 bg-[#25D366] text-white px-6 py-3 rounded-full font-medium hover:opacity-90 transition-opacity whitespace-nowrap"
            >
              <MessageCircle className="w-4 h-4" /> Chat with us
            </a>
          </div>
          <p className="text-bark/70 text-xs mt-6">
            17/11 BeautyBarn · Port Harcourt, Nigeria · <Link to="/contact" className="underline hover:text-terracotta">Contact us</Link>
          </p>
        </Reveal>
      </div>
    </div>
  );
}
